import React, { useRef, useState } from 'react';
import { api, resolveAsset } from '../api/client';
import { useAuth } from '../state/auth';
import { useT } from '../i18n';

// Shared "duplicate this preview / story" dialog (previews table + story
// editor). The copy is a full book copy — scenes, images, cover and back-cover
// design, personalization, contact — so the first thing to decide is what the
// copy IS: its generation status, whether it's test data, and whether it should
// look paid. Marking it paid creates a linked admin_manual order on the server
// (that's what "paid" means for a preview), which is admin-only.
//
// The second, optional part is a RECAST: the same book starring a different
// child. The hero's name is swapped in the text immediately; a gender change is
// re-agreed by the LLM; and with a photo attached every illustration is
// repainted with that child — identical composition, pose, background and
// clothing, only the child is different. That runs in the background and
// reports progress on the copy (see story.recast).
export default function DuplicateStoryModal({ storyId, label, defaultStatus, defaultIsTest, defaultName, defaultGender, onClose, onDone }) {
  const t = useT();
  const { isAdmin } = useAuth();
  const STATUS_OPTIONS = [
    ['previewing', t.adminStatusPreviewing],
    ['preview_ready', t.adminStatusPreviewReady],
    ['finalizing', t.adminStatusFinalizing],
    ['finalized', t.adminStatusFinalized],
    ['iterating', t.adminStatusIterating],
    ['locked_for_print', t.adminStatusLockedForPrint],
    ['failed', t.adminStatusFailed],
  ];
  const known = STATUS_OPTIONS.some(([v]) => v === defaultStatus);
  const [status, setStatus] = useState(known ? defaultStatus : 'preview_ready');
  const [isTest, setIsTest] = useState(!!defaultIsTest);
  const [paid, setPaid] = useState(false);

  // Recast (different child) — off by default: a plain duplicate is the common case.
  const [recast, setRecast] = useState(false);
  const [name, setName] = useState(defaultName || '');
  const [gender, setGender] = useState(defaultGender || '');
  const [photoUrl, setPhotoUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [regenerateImages, setRegenerateImages] = useState(true);
  const fileRef = useRef(null);

  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('image', file);
      fd.append('prefix', 'recast-child');
      const res = await api.postForm('/api/admin/uploads/image', fd);
      setPhotoUrl(res.url);
    } catch (e) {
      setError(e.message || t.adminDuplicateFailed);
    } finally {
      setUploading(false);
    }
  };

  const run = async () => {
    setWorking(true);
    setError('');
    try {
      const body = { status, isTest, paid };
      if (recast) {
        body.recast = {
          name: name.trim(),
          gender,
          photoUrl,
          regenerateImages: regenerateImages && !!photoUrl,
        };
      }
      const result = await api.post(`/api/admin/previews/${storyId}/duplicate`, body);
      onDone(result);
    } catch (e) {
      setError(e.message || t.adminDuplicateFailed);
    } finally {
      setWorking(false);
    }
  };

  const busy = working || uploading;

  return (
    <div className="admin-modal-overlay" onClick={() => !busy && onClose()}>
      <div className="admin-modal" onClick={(e) => e.stopPropagation()} style={{ maxHeight: '86vh', overflowY: 'auto' }}>
        <h3 className="admin-modal-title">{t.adminDuplicateTitle}</h3>
        <p className="admin-modal-message">
          {t.adminDuplicateMessage}
          {label ? <><br /><strong>{label}</strong></> : null}
        </p>

        <label style={{ display: 'block', margin: '12px 0 4px', fontSize: '0.9rem', fontWeight: 600 }}>
          {t.adminDuplicateStatusLabel}
        </label>
        <select
          className="admin-title-input"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          disabled={busy}
          style={{ width: '100%' }}
        >
          {STATUS_OPTIONS.map(([value, statusLabel]) => (
            <option key={value} value={value}>{statusLabel}</option>
          ))}
        </select>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, cursor: 'pointer', fontSize: '0.9rem' }}>
          <input type="checkbox" checked={isTest} onChange={(e) => setIsTest(e.target.checked)} disabled={busy} />
          <span>{t.adminDuplicateTestLabel}</span>
        </label>

        {/* Money-touching: the server rejects a paid copy from an editor. */}
        {isAdmin && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, cursor: 'pointer', fontSize: '0.9rem' }}>
            <input type="checkbox" checked={paid} onChange={(e) => setPaid(e.target.checked)} disabled={busy} />
            <span>{t.adminDuplicatePaidLabel}</span>
          </label>
        )}

        {/* ── Recast: same book, different child ── */}
        <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #eee' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.9rem', fontWeight: 600 }}>
            <input type="checkbox" checked={recast} onChange={(e) => setRecast(e.target.checked)} disabled={busy} />
            <span>{t.adminDuplicateRecastLabel}</span>
          </label>

          {recast && (
            <div style={{ marginTop: 10, display: 'grid', gap: 10 }}>
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontSize: '0.85rem' }}>{t.adminDuplicateChildName}</label>
                <input
                  className="admin-title-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={busy}
                  dir="rtl"
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontSize: '0.85rem' }}>{t.adminDuplicateChildGender}</label>
                <select
                  className="admin-title-input"
                  value={gender}
                  onChange={(e) => setGender(e.target.value)}
                  disabled={busy}
                  style={{ width: '100%' }}
                >
                  <option value="ولد">{t.adminGenderBoy}</option>
                  <option value="بنت">{t.adminGenderGirl}</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontSize: '0.85rem' }}>{t.adminDuplicateChildPhoto}</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  {photoUrl && (
                    <img
                      src={resolveAsset(photoUrl)}
                      alt=""
                      style={{ width: 56, height: 56, borderRadius: 8, objectFit: 'cover', background: '#f3f4f6' }}
                    />
                  )}
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={(e) => uploadPhoto(e.target.files?.[0])}
                  />
                  <button type="button" className="admin-btn-secondary" onClick={() => fileRef.current?.click()} disabled={busy}>
                    {uploading ? '…' : (photoUrl ? t.adminDuplicateChangePhoto : t.adminDuplicateUploadPhoto)}
                  </button>
                </div>
              </div>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: photoUrl ? 'pointer' : 'default', fontSize: '0.9rem', opacity: photoUrl ? 1 : 0.55 }}>
                <input
                  type="checkbox"
                  checked={regenerateImages && !!photoUrl}
                  onChange={(e) => setRegenerateImages(e.target.checked)}
                  disabled={busy || !photoUrl}
                  style={{ marginTop: 3 }}
                />
                <span>
                  {t.adminDuplicateRedrawLabel}
                  <br />
                  <span style={{ color: '#888', fontSize: '0.8rem' }}>{t.adminDuplicateRedrawHint}</span>
                </span>
              </label>
            </div>
          )}
        </div>

        {error && <p className="admin-modal-error">{error}</p>}
        <div className="admin-modal-actions">
          <button type="button" className="admin-btn-secondary" onClick={onClose} disabled={busy}>
            {t.adminCancel}
          </button>
          <button type="button" className="admin-btn-primary" onClick={run} disabled={busy}>
            {working ? '…' : t.adminDuplicateSubmit}
          </button>
        </div>
      </div>
    </div>
  );
}
